<!-- Audio Player Modal -->
<app-modal
  [isOpen]="isOpen"
  borderRadius="1.75rem"
  contentClass="audio-player-content"
  headerClass="audio-player-header"
  [customHeader]="true"
  (closed)="onClose()"
>
  <!-- Top Navigation Bar: Close (chevron down) & More Options (three dots) -->
  <div modal-header class="audio-header-content flex items-center justify-between w-full">
    <button type="button" class="header-icon-btn flex items-center justify-center" (click)="onClose()" title="Close player" aria-label="Close">
      <app-icon name="chevron-down" class="w-5 h-5"></app-icon>
    </button>

    <span class="player-header-title">Now Playing</span>

    <div class="more-menu-container relative">
      <button type="button" class="header-icon-btn flex items-center justify-center" [class.active]="showMoreMenu"
        (click)="toggleMoreMenu($event)" title="More options" aria-label="More options">
        <app-icon name="threedot" class="w-5 h-5"></app-icon>
      </button>

      <!-- Three-Dots Dropdown Menu -->
      @if (showMoreMenu) {
      <div class="more-menu-dropdown" (click)="$event.stopPropagation()">
        <!-- Call Details Option -->
        @if (metadata) {
        <button type="button" class="menu-item-btn" (click)="toggleMetadataPanel()">
          <app-icon name="info" class="w-4 h-4"></app-icon>
          <span>Call Details</span>
        </button>
        }

        <!-- Inline Volume Control -->
        <div class="menu-volume-control">
          <div class="menu-volume-header">
            <div class="flex items-center gap-1.5">
              <app-icon name="volume-2" class="w-4 h-4 text-white/90"></app-icon>
              <span class="text-xs font-medium text-white/90">Volume</span>
            </div>
            <span class="text-xs font-mono text-white/70">{{ Math.round(volume * 100) }}%</span>
          </div>
          <input type="range" min="0" max="1" step="0.05" [value]="volume" (input)="setVolume($event)"
            class="volume-slider menu-volume-slider" aria-label="Volume slider" />
        </div>

        <!-- Speed Selection Chips -->
        <div class="menu-speed-section">
          <span class="menu-section-label">Playback Speed</span>
          <div class="speed-chips-grid">
            @for (rate of playbackRates; track rate) {
            <button type="button" class="speed-chip" [class.active]="playbackRate === rate"
              (click)="setPlaybackRate(rate)">
              {{ rate }}x
            </button>
            }
          </div>
        </div>

        <div class="menu-divider"></div>

        <!-- Download Recording Option -->
        @if (audioUrl) {
        <button type="button" class="menu-item-btn" (click)="downloadRecording(); closeMoreMenu()" [disabled]="isLoading"
          title="Download audio recording">
          <app-icon name="download" class="w-4 h-4"></app-icon>
          <span>Download Audio</span>
        </button>
        }

        <!-- Keyboard Shortcuts Option -->
        <button type="button" class="menu-item-btn" (click)="toggleKeyboardShortcuts()">
          <app-icon name="help-circle" class="w-4 h-4"></app-icon>
          <span>Keyboard Shortcuts</span>
        </button>
      </div>
      }
    </div>
  </div>

  <!-- Hidden Audio Element -->
  <audio #audioElement preload="metadata" crossorigin="anonymous"></audio>

  <!-- Error State -->
  @if (hasError) {
  <div class="error-container">
    <div class="error-icon">
      <app-icon name="alert-circle" class="w-8 h-8"></app-icon>
    </div>
    <div class="error-content">
      <p class="empty-title">
        @if (isAuthenticationError) {
        Authentication Required
        } @else {
        Unable to load recording
        }
      </p>
      <p class="text-sm error-message mb-4">{{ errorMessage }}</p>
      <div class="error-actions">
        @if (isAuthenticationError) {
        <button class="retry-btn" (click)="redirectToLogin()">
          <app-icon name="log-in" class="w-4 h-4 mr-2"></app-icon>
          Go to Login
        </button>
        } @else {
        <button class="retry-btn" (click)="initializeAudio()">
          <app-icon name="refresh-cw" class="w-4 h-4 mr-2"></app-icon>
          Try Again
        </button>
        }
      </div>
    </div>
  </div>
  } @else {

  <!-- Central Rounded Artwork / Visualizer Card (Spotify/Wynk style) -->
  <div class="player-artwork-card">
    @if (showMetadataPanel && metadata) {
    <!-- Call Details Panel (displayed inside artwork area) -->
    <div class="artwork-overlay-panel">
      <div class="panel-header">
        <span class="panel-title">Call Information</span>
        <button type="button" class="panel-close-btn" (click)="toggleMetadataPanel()" aria-label="Close details">
          <app-icon name="x" class="w-4 h-4"></app-icon>
        </button>
      </div>
      <div class="panel-content custom-scrollbar">
        <div class="metadata-grid">
          @if (metadata.patientName) {
          <div class="metadata-item">
            <span class="metadata-label">Patient</span>
            <span class="metadata-value">{{ metadata.patientName }}</span>
          </div>
          }
          @if (metadata.telecallerName) {
          <div class="metadata-item">
            <span class="metadata-label">Telecaller</span>
            <span class="metadata-value">{{ metadata.telecallerName }}</span>
          </div>
          }
          @if (metadata.callDate) {
          <div class="metadata-item">
            <span class="metadata-label">Date</span>
            <span class="metadata-value">{{ metadata.callDate }}</span>
          </div>
          }
          @if (metadata.callTime) {
          <div class="metadata-item">
            <span class="metadata-label">Time</span>
            <span class="metadata-value">{{ metadata.callTime }}</span>
          </div>
          }
          @if (metadata.duration) {
          <div class="metadata-item">
            <span class="metadata-label">Duration</span>
            <span class="metadata-value">{{ metadata.duration }}</span>
          </div>
          }
          @if (metadata.callType) {
          <div class="metadata-item">
            <span class="metadata-label">Type</span>
            <span class="metadata-value">{{ metadata.callType }}</span>
          </div>
          }
          @if (metadata.outcome) {
          <div class="metadata-item col-span-2">
            <span class="metadata-label">Outcome</span>
            <span class="metadata-value">{{ metadata.outcome }}</span>
          </div>
          }
        </div>
      </div>
    </div>
    } @else if (showKeyboardShortcuts) {
    <!-- Keyboard Shortcuts Help Panel (displayed inside artwork area) -->
    <div class="artwork-overlay-panel">
      <div class="panel-header">
        <span class="panel-title">Keyboard Shortcuts</span>
        <button type="button" class="panel-close-btn" (click)="toggleKeyboardShortcuts()" aria-label="Close shortcuts">
          <app-icon name="x" class="w-4 h-4"></app-icon>
        </button>
      </div>
      <div class="panel-content custom-scrollbar">
        <div class="shortcuts-grid">
          <div class="shortcut-item"><kbd>Space</kbd><span>Play / Pause</span></div>
          <div class="shortcut-item"><kbd>←</kbd><span>Rewind {{ skipBackwardSeconds }}s</span></div>
          <div class="shortcut-item"><kbd>→</kbd><span>Forward {{ skipForwardSeconds }}s</span></div>
          <div class="shortcut-item"><kbd>↑</kbd><span>Volume Up</span></div>
          <div class="shortcut-item"><kbd>↓</kbd><span>Volume Down</span></div>
          <div class="shortcut-item"><kbd>R</kbd><span>Replay</span></div>
          <div class="shortcut-item"><kbd>0-9</kbd><span>Jump to %</span></div>
        </div>
      </div>
    </div>
    } @else {
    <!-- Standard Visual Artwork (3D Glass Phone on Glowing Pedestal) -->
    <div class="artwork-inner" [class.playing]="isPlaying">
      <div class="artwork-3d-wrapper">
        <img src="/images/phone-3d-glass.jpg" alt="Call Recording Visualizer" class="artwork-3d-image" />
        <div class="artwork-3d-ring"></div>
      </div>
    </div>
    }
  </div>

  <!-- Track Title & Metadata Badges (cleanly positioned below artwork) -->
  <div class="track-info-section">
    <div class="track-header-row">
      <div class="track-text">
        <h2 class="track-title" [title]="title">{{ title || 'Call Recording' }}</h2>
        <div class="track-sub-row">
          @if (metadata?.patientName) {
          <button type="button" class="metadata-pill" (click)="toggleMetadataPanel()" title="View patient details">
            <app-icon name="user" class="w-3.5 h-3.5"></app-icon>
            <span>{{ metadata?.patientName }}</span>
          </button>
          }
          @if (metadata?.telecallerName) {
          <button type="button" class="metadata-pill" (click)="toggleMetadataPanel()" title="View telecaller details">
            <app-icon name="telecaller" class="w-3.5 h-3.5"></app-icon>
            <span>{{ metadata?.telecallerName }}</span>
          </button>
          }
          @if (!metadata && subtitle) {
          <span class="track-subtitle">{{ subtitle }}</span>
          }
        </div>
      </div>

      @if (metadata) {
      <button type="button" class="info-toggle-btn" [class.active]="showMetadataPanel"
        (click)="toggleMetadataPanel()" title="View call details" aria-label="View call details">
        <app-icon name="info" class="w-4 h-4"></app-icon>
      </button>
      }
    </div>
  </div>

  <!-- Progress Scrubber Bar & Timestamps -->
  <div class="progress-section">
    <div class="progress-container">
      <div #progressBar class="progress-bar" (click)="seek($event)" (mousedown)="startDrag($event)"
        (touchstart)="startDrag($event)" (mousemove)="showHoverPreview($event)" (mouseleave)="hideHoverPreview()"
        role="slider" tabindex="0" aria-label="Seek audio position"
        [attr.aria-valuemin]="0" [attr.aria-valuemax]="duration" [attr.aria-valuenow]="currentTime"
        [attr.aria-valuetext]="formatTime(currentTime) + ' of ' + formatTime(duration)"
        (keydown.home)="$event.preventDefault(); rewind(duration)"
        (keydown.end)="$event.preventDefault(); fastForward(duration)">
        <div class="progress-fill" [style.width.%]="progress"></div>
        <div class="progress-thumb" [style.left.%]="progress" [class.dragging]="isDragging"></div>

        <!-- Hover preview tooltip -->
        @if (hoverTime !== null && !isDragging) {
        <div class="progress-tooltip" [style.left.%]="(hoverTime / duration) * 100">
          {{ formatTime(hoverTime) }}
        </div>
        }
      </div>
    </div>

    <!-- Timestamps directly under progress scrubber (00:00 on left, 15:26 on right) -->
    <div class="time-display">
      <span class="current-time">{{ formatTime(currentTime) }}</span>
      <span class="duration">{{ formatTime(duration) }}</span>
    </div>
  </div>

  <!-- Single-Row Primary Controls: Replay | -5s | Play/Pause (Center) | +5s | Speed -->
  <div class="single-row-controls">
    <!-- 1. Replay / Restart Button -->
    <button type="button" class="control-icon-btn" (click)="replay()" title="Restart from beginning"
      [disabled]="isLoading || hasError" aria-label="Replay">
      <app-icon name="refresh-ccw" class="w-5 h-5"></app-icon>
    </button>

    <!-- 2. Skip Backward (-5s) Button -->
    <button type="button" class="control-icon-btn" (click)="rewind()" title="Rewind {{ skipBackwardSeconds }}s"
      [disabled]="isLoading || hasError" aria-label="Rewind">
      <app-icon name="rotate-ccw" class="w-5 h-5"></app-icon>
      <span class="control-pill-sub">-{{ skipBackwardSeconds }}</span>
    </button>

    <!-- 3. Prominent Center Play / Pause Glass Button -->
    <button type="button" class="play-pause-btn" (click)="togglePlayPause()" [disabled]="isLoading"
      aria-label="Play or pause">
      @if (isLoading) {
      <div class="loading-spinner"></div>
      } @else if (isPlaying) {
      <app-icon name="pause" class="w-7 h-7"></app-icon>
      } @else {
      <app-icon name="play" class="w-7 h-7"></app-icon>
      }
    </button>

    <!-- 4. Skip Forward (+5s) Button -->
    <button type="button" class="control-icon-btn" (click)="fastForward()" title="Forward {{ skipForwardSeconds }}s"
      [disabled]="isLoading || hasError" aria-label="Fast forward">
      <app-icon name="rotate-cw" class="w-5 h-5"></app-icon>
      <span class="control-pill-sub">+{{ skipForwardSeconds }}</span>
    </button>

    <!-- 5. Speed Pill (click cycles through rates: 1x, 1.25x, 1.5x, 2x, etc.) -->
    <button type="button" class="speed-pill-btn" (click)="cyclePlaybackRate()"
      title="Click to change playback speed (Current: {{ playbackRate }}x)" aria-label="Playback speed">
      <span>{{ playbackRate }}x</span>
    </button>
  </div>
  }
</app-modal>
